import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { RefreshIcon } from './Icons';
import Pagination from './Pagination';

const DEFAULT_PAGE_SIZE = 50;
// Razorpay is the only system of record for a refund (see
// server/ivr/razorpayClient.js) — nothing is stored locally — so poll it the
// same way PaymentLinks.js does, so a refund moving from pending to
// processed shows up without a manual refresh.
const AUTO_REFRESH_MS = 10000;
const REFUNDS_PAGE_SIZE = 100; // Razorpay's per-call maximum
const MAX_REFUND_PAGES = 50; // safety cap: 5,000 refunds

const STATUS_LABELS = {
  pending: 'Pending',
  processed: 'Processed',
  failed: 'Failed',
};

// Reuses the existing `.status` pill classes (App.css), same as PaymentLinks.js.
const STATUS_CLASSES = {
  pending: 'attempted',
  processed: 'answered',
  failed: 'missed',
};

const formatAmount = (refund) => {
  const value = Number(refund.amount_rupees);
  if (Number.isNaN(value)) return '-';
  const formatted = value.toLocaleString('en-IN', { maximumFractionDigits: 2 });
  return refund.currency && refund.currency !== 'INR' ? `${formatted} ${refund.currency}` : `₹${formatted}`;
};

const formatDateTime = (unixSeconds) => {
  if (!unixSeconds) return '-';
  return new Date(unixSeconds * 1000).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Read-only view of Razorpay refunds — every refund across all payments,
// fetched fresh from Razorpay on load and on each poll.
function Refunds() {
  const { user, authFetch } = useAuth();
  // The aggregate refunded amount is superadmin-only; other roles that have
  // the Refunds page still see the individual rows, just not this total.
  const isSuperadmin = user?.role === 'superadmin';
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Razorpay caps one call at 100 refunds, so the full history is loaded by
  // walking `skip` until a short page comes back. Only the initial load /
  // manual Refresh does this; the 10s poll re-fetches just the newest page
  // and merges it in by id, so new refunds appear and recent status changes
  // (pending -> processed) update without re-downloading everything.
  const fetchRefundPage = async (skip) => {
    const response = await authFetch(`/api/ivr/refunds?count=${REFUNDS_PAGE_SIZE}&skip=${skip}`);
    const body = await response.json().catch(() => null);
    if (!response.ok) throw new Error(body?.error || 'Failed to fetch refunds.');
    return Array.isArray(body?.refunds) ? body.refunds : [];
  };

  const fetchRefunds = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      if (silent) {
        const latest = await fetchRefundPage(0);
        setRefunds((prev) => {
          const byId = new Map(prev.map((refund) => [refund.id, refund]));
          latest.forEach((refund) => byId.set(refund.id, refund));
          return Array.from(byId.values()).sort((a, b) => b.created_at - a.created_at);
        });
      } else {
        const all = [];
        for (let i = 0; i < MAX_REFUND_PAGES; i += 1) {
          const batch = await fetchRefundPage(i * REFUNDS_PAGE_SIZE);
          all.push(...batch);
          if (batch.length < REFUNDS_PAGE_SIZE) break;
        }
        setRefunds(all);
      }
      setError('');
    } catch (err) {
      if (!silent) setRefunds([]);
      setError(err.message || 'Failed to fetch refunds.');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchRefunds();
    const interval = setInterval(() => fetchRefunds({ silent: true }), AUTO_REFRESH_MS);
    return () => clearInterval(interval);
  }, []);

  const statusCounts = useMemo(() => {
    const counts = { all: refunds.length, pending: 0, processed: 0, failed: 0 };
    refunds.forEach((refund) => {
      if (counts[refund.status] !== undefined) counts[refund.status] += 1;
    });
    return counts;
  }, [refunds]);

  const totalRefunded = useMemo(
    () => refunds.filter((r) => r.status === 'processed').reduce((sum, r) => sum + (r.amount_rupees || 0), 0),
    [refunds]
  );

  const filteredRefunds = useMemo(() => {
    const term = search.trim().toLowerCase();
    return refunds.filter((refund) => {
      if (statusFilter !== 'all' && refund.status !== statusFilter) return false;
      if (!term) return true;
      const haystack = [
        refund.id,
        refund.payment_id,
        refund.receipt,
        refund.acquirer_data?.rrn,
        refund.customer_name,
        refund.customer_phone,
        refund.customer_email,
        refund.order_number,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [refunds, statusFilter, search]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, search]);

  const totalPages = Math.max(1, Math.ceil(filteredRefunds.length / pageSize));
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };

  const pageRows = filteredRefunds.slice((page - 1) * pageSize, page * pageSize);

  return (
    <>
      <div className="incoming-filters-bar">
        <button type="button" className="btn btn-secondary" onClick={() => fetchRefunds()} disabled={loading}>
          <RefreshIcon width={14} height={14} />
          Refresh
        </button>
        <input
          type="text"
          placeholder="Search by customer, phone, order no., refund id, payment id…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="search-input"
        />
      </div>

      <div className="stat-cards">
        <button type="button" className={`stat-card ${statusFilter === 'all' ? 'active' : ''}`} onClick={() => setStatusFilter('all')}>
          <div className="stat-value">{statusCounts.all}</div>
          <div className="stat-label">All</div>
        </button>
        <button type="button" className={`stat-card ${statusFilter === 'pending' ? 'active' : ''}`} onClick={() => setStatusFilter('pending')}>
          <div className="stat-value">{statusCounts.pending}</div>
          <div className="stat-label">Pending</div>
        </button>
        <button type="button" className={`stat-card answered ${statusFilter === 'processed' ? 'active' : ''}`} onClick={() => setStatusFilter('processed')}>
          <div className="stat-value">{statusCounts.processed}</div>
          <div className="stat-label">Processed</div>
        </button>
        <button type="button" className={`stat-card critical ${statusFilter === 'failed' ? 'active' : ''}`} onClick={() => setStatusFilter('failed')}>
          <div className="stat-value">{statusCounts.failed}</div>
          <div className="stat-label">Failed</div>
        </button>
        {isSuperadmin && (
          <div className="stat-card">
            <div className="stat-value">₹{totalRefunded.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>
            <div className="stat-label">Total Refunded</div>
          </div>
        )}
      </div>

      {error && <div className="call-feedback call-feedback-error">{error}</div>}

      {loading ? (
        <div className="loading-state">Loading…</div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Created At</th>
                <th>Customer</th>
                <th>Order No.</th>
                <th>Refund Id</th>
                <th>Payment Id</th>
                <th>Amount</th>
                <th>Speed</th>
                <th>RRN</th>
                <th>Receipt</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: 'center' }}>
                    {error ? 'Could not load refunds.' : 'No refunds match this filter.'}
                  </td>
                </tr>
              ) : (
                pageRows.map((refund) => (
                  <tr key={refund.id}>
                    <td>{formatDateTime(refund.created_at)}</td>
                    <td>
                      <div>{refund.customer_name || '-'}</div>
                      {refund.customer_phone && <div className="stat-label">{refund.customer_phone}</div>}
                    </td>
                    <td title={refund.order_note || undefined}>
                      {refund.order_number || (refund.order_note ? 'Order not placed' : '-')}
                    </td>
                    <td>{refund.id}</td>
                    <td>{refund.payment_id || '-'}</td>
                    <td>{formatAmount(refund)}</td>
                    <td>{refund.speed_processed || refund.speed_requested || '-'}</td>
                    <td>{refund.acquirer_data?.rrn || '-'}</td>
                    <td>{refund.receipt || '-'}</td>
                    <td>
                      <span className={`status ${STATUS_CLASSES[refund.status] || 'attempted'}`}>
                        {STATUS_LABELS[refund.status] || refund.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        totalItems={filteredRefunds.length}
      />
    </>
  );
}

export default Refunds;
